interface TrendPoint {
  day: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface FindingTrendsChartProps {
  data: TrendPoint[];
}

type SeverityKey = 'critical' | 'high' | 'medium' | 'low';

// Stack from bottom up: low → medium → high → critical so the most severe
// sits on top. Stroke colors match each band's fill so the top edge reads
// as a line for that severity.
const SERIES: Array<{ key: SeverityKey; label: string; fill: string; stroke: string; dot: string }> = [
  { key: 'low', label: 'Low', fill: 'fill-blue-500/30', stroke: 'stroke-blue-500', dot: 'bg-blue-500' },
  { key: 'medium', label: 'Medium', fill: 'fill-yellow-500/30', stroke: 'stroke-yellow-500', dot: 'bg-yellow-500' },
  { key: 'high', label: 'High', fill: 'fill-orange-500/30', stroke: 'stroke-orange-500', dot: 'bg-orange-500' },
  { key: 'critical', label: 'Critical', fill: 'fill-red-500/40', stroke: 'stroke-red-500', dot: 'bg-red-500' },
];

const VIEW_W = 600;
const VIEW_H = 180;
const PAD_T = 8;
const PAD_B = 18;
const PAD_L = 28;
const PAD_R = 8;
const PLOT_W = VIEW_W - PAD_L - PAD_R;
const PLOT_H = VIEW_H - PAD_T - PAD_B;

export function FindingTrendsChart({ data }: FindingTrendsChartProps) {
  // Normalize to a fixed 30-day window so the x-axis is stable even when
  // some days have no findings.
  const days: TrendPoint[] = [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    const found = data.find((p) => p.day === key);
    days.push(found ?? { day: key, critical: 0, high: 0, medium: 0, low: 0 });
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-44 text-sm text-muted-foreground">
        No findings in the last 30 days
      </div>
    );
  }

  const totals = days.map((d) => d.critical + d.high + d.medium + d.low);
  const maxTotal = Math.max(...totals, 1);

  // Build cumulative stacks per day in series order.
  const stacked = days.map((d) => {
    const lo = d.low;
    const me = lo + d.medium;
    const hi = me + d.high;
    const cr = hi + d.critical;
    return { low: lo, medium: me, high: hi, critical: cr };
  });

  const x = (i: number) => PAD_L + (i / (days.length - 1)) * PLOT_W;
  const y = (v: number) => PAD_T + PLOT_H - (v / maxTotal) * PLOT_H;

  const areaPath = (top: number[], bottom: number[]) => {
    const up = top.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(' ');
    const down = bottom
      .slice()
      .reverse()
      .map((v, i) => `L${x(bottom.length - 1 - i).toFixed(2)},${y(v).toFixed(2)}`)
      .join(' ');
    return `${up} ${down} Z`;
  };

  const linePath = (vals: number[]) =>
    vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(' ');

  // Y-axis ticks: 0, mid, max — kept light to avoid chart clutter.
  const yTicks = [0, Math.round(maxTotal / 2), maxTotal];

  return (
    <div className="space-y-3">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="w-full h-44"
        role="img"
        aria-label="Findings opened per day over the last 30 days, stacked area by severity"
      >
        {/* gridlines */}
        {yTicks.map((t) => (
          <g key={t}>
            <line
              x1={PAD_L}
              x2={VIEW_W - PAD_R}
              y1={y(t)}
              y2={y(t)}
              className="stroke-border"
              strokeWidth={0.5}
              strokeDasharray="2 3"
            />
            <text
              x={PAD_L - 4}
              y={y(t) + 3}
              textAnchor="end"
              className="fill-muted-foreground text-[9px]"
            >
              {t}
            </text>
          </g>
        ))}

        {/* stacked areas + top-edge lines, drawn in series order so the most
         * severe sits on top. Each band's bottom is the previous cumulative
         * line (or zero for the first series). */}
        {SERIES.map((s, idx) => {
          const top = stacked.map((p) => p[s.key]);
          const bottom =
            idx === 0
              ? new Array(stacked.length).fill(0)
              : stacked.map((p) => p[SERIES[idx - 1].key]);
          return (
            <g key={s.key}>
              <path d={areaPath(top, bottom)} className={s.fill} />
              <path
                d={linePath(top)}
                className={s.stroke}
                fill="none"
                strokeWidth={1.2}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}

        {/* axis baseline */}
        <line
          x1={PAD_L}
          x2={VIEW_W - PAD_R}
          y1={y(0)}
          y2={y(0)}
          className="stroke-border"
          strokeWidth={0.5}
        />
      </svg>

      {/* x-axis labels: first / mid / last */}
      <div className="flex justify-between text-[10px] text-muted-foreground px-1">
        <span>{formatDay(days[0].day)}</span>
        <span>{formatDay(days[Math.floor(days.length / 2)].day)}</span>
        <span>{formatDay(days[days.length - 1].day)}</span>
      </div>

      {/* legend with 30-day totals */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {[...SERIES].reverse().map((s) => {
          const total = days.reduce((sum, d) => sum + d[s.key], 0);
          return (
            <span key={s.key} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-sm ${s.dot}`} />
              {s.label}: {total}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function formatDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
